import { forwardRef, useState, useEffect } from 'react';
import { Check, X, Pencil } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Select } from './Select';

export interface EditableSelectOption {
  value: string;
  label: string;
}

export interface EditableSelectProps {
  value: string;
  options: EditableSelectOption[];
  onSave: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const EditableSelect = forwardRef<HTMLDivElement, EditableSelectProps>(
  ({ value, options, onSave, placeholder = 'Cliquez pour modifier', className }, ref) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    useEffect(() => {
      setEditValue(value);
    }, [value]);

    const handleStartEdit = () => {
      setEditValue(value);
      setIsEditing(true);
    };

    const handleSave = () => {
      onSave(editValue);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(value);
      setIsEditing(false);
    };

    const displayValue = options.find((opt) => opt.value === value)?.label || value;

    if (isEditing) {
      return (
        <div ref={ref} className={cn('flex items-center gap-2', className)}>
          <div className="flex-1">
            <Select
              value={editValue}
              onChange={setEditValue}
              options={options}
              allowEmpty={false}
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={handleSave}
              className={cn(
                'rounded-lg p-1.5 text-success-600',
                'hover:bg-success-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-500'
              )}
              aria-label="Sauvegarder"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancel}
              className={cn(
                'rounded-lg p-1.5 text-danger-600',
                'hover:bg-danger-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500'
              )}
              aria-label="Annuler"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        onClick={handleStartEdit}
        onKeyDown={(e) => e.key === 'Enter' && handleStartEdit()}
        tabIndex={0}
        role="button"
        className={cn(
          'group flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2',
          'hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          'transition-colors',
          className
        )}
      >
        <span className={cn('text-sm', displayValue ? 'text-dark-500' : 'text-neutral-400')}>
          {displayValue || placeholder}
        </span>
        <Pencil className="h-3.5 w-3.5 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  }
);

EditableSelect.displayName = 'EditableSelect';

export { EditableSelect };
